// @ts-ignore
import processEnv from 'processEnv';
import { Platform } from 'models/platforms/platform';
import { CcSettings } from 'config/platforms/deviceIntegration';
import { AppData, Router, Storage } from '@lightningjs/sdk';
import { DeviceInfo } from 'models/platforms/deviceInfo';
import { Manufacturer } from 'models/platforms/manufacturer';
import {
  getHomeHistoryEntry,
  getLiveEventHistoryEntry,
  getLivePlaybackHistoryEntry,
  getPageIdFromHash,
  navigateHome,
  shouldShowLogin,
} from 'support/routerUtils';
import { RoutePath } from 'config/routes';
import { constants } from 'aliases';
import {
  ErrorCode,
  ReportableError,
  reportError,
} from 'services/analytics/reportingServicePage';
import { DrmType } from 'services/drm/DRMService';
import App from 'App';
import { PageId } from 'types/pageId';
import { createDeepLinkUrl } from 'support/deepLinkUtils';
import { getSimulatedKeyEvent } from 'support/keyEventUtils';
import {
  ONE_TRUST_NAV_KEYS,
  toOneTrustKeys,
} from 'models/platforms/oneTrustKeyMappings';
import { OneTrustRemoteKeys } from 'types/oneTrust';

export abstract class AbstractDeviceIntegration {
  protected abstract readonly platform: Platform;
  protected deviceId = '';
  // TODO: Hook privacy change listener up for LG, Tizen, and Vizio
  protected privacyChangeListener: ((optOut: boolean) => void) | undefined;
  protected isHovering = false;

  protected deviceInfo: DeviceInfo = {
    manufacturer: Manufacturer.UNKNOWN,
    model: '',
    osVersion: '',
    adId: '',
    isLat: false,
    ifaType: '',
    ip: '',
  };

  protected _ccSettings: Partial<CcSettings> = {};

  abstract getDeviceId(): string;

  abstract hasFlickerIssue(): boolean;

  abstract isLowPerformanceModel(): boolean;

  abstract isLowMemoryModel(): boolean;

  abstract getAppId(): string;

  abstract getSubmittedAppVersion(): string;

  abstract load(): Promise<void>;

  abstract onAppActive(): void;

  abstract beforeAppClose(callback: (showPopup: boolean) => void): void;

  abstract closeApp(): void;

  abstract handleDeepLink(): boolean;

  abstract handleNetworkChange(callback: (arg: boolean) => void): void;

  abstract setScreenSaver(state: boolean): void;

  abstract getAnnouncerEnabled(): boolean;

  abstract shouldAnnounceContext(): boolean;

  abstract shouldAnnounceSeeking(): boolean;

  abstract handleAnnouncerChange(callback: (arg: boolean) => void): void;

  abstract handleCcChange(callback: (enabled: boolean) => void): void;

  abstract useTextTrackComponent(): boolean;

  abstract onAdPodStart(): void;

  abstract onAdPodEnd(): void;

  abstract onPlayerExit(): void;

  abstract getKeyMapping(): Record<number, string>;

  canUseBrightcoveSdk(): boolean {
    const useBcOverride = processEnv['APP_USE_BRIGHTCOVE_SDK_OVERRIDE'];
    if (!!useBcOverride) {
      return useBcOverride === 'true';
    }

    /* 
    Generally this will target:
    - Vizio 2020+ (and some of the 2018-2019 devices) https://docs.developer.vizio.com/docs/vizio-tv-model-information
    - Tizen 2021+ / Tizen 6.0+ https://developer.samsung.com/smarttv/develop/specifications/web-engine-specifications.html
    - LG 2021+ / WebOS 6.x https://webostv.developer.lge.com/develop/specifications/web-api-and-web-engine
    */
    const chromeVersion = this.getChromeVersion();
    return (
      !!chromeVersion &&
      chromeVersion >= constants.brightcove.minimumBrowserVersionForSdk
    );
  }

  canUseOneTrustSdk(): boolean {
    const useOneTrustOverride = processEnv['APP_USE_ONETRUST_OVERRIDE'];
    if (!!useOneTrustOverride) {
      return useOneTrustOverride === 'true';
    }

    return this.isOneTrustSupportedOs();
  }

  // OneTrust only publishes an SDK package for Samsung 2018+ (Tizen 4.0+) and
  // LG 2020+ (webOS 5.0+), and warns that older browsers may need polyfills.
  // https://developer.onetrust.com/onetrust/docs/tizen-and-webos-sdk-new
  protected isOneTrustSupportedOs(): boolean {
    return false;
  }

  /**
   * The keys OneTrust's own listener should act on, in its vocabulary. Derived
   * from the platform's key map so the two can't drift apart, and so no
   * platform hands the SDK a code its remote never sends. Override only to add
   * a key that map has no name for.
   */
  getOneTrustKeyMapping(): OneTrustRemoteKeys {
    return {
      ...ONE_TRUST_NAV_KEYS,
      ...toOneTrustKeys(this.getKeyMapping()),
    };
  }

  getDrmType(): DrmType {
    return DrmType.WIDEVINE;
  }

  getChromeVersion(): number | null {
    const extractedVersion = navigator.userAgent.match(
      /Chrom(?:e|ium)\/([0-9]+)\.([0-9]+)\.([0-9]+)\.([0-9]+)/,
    );
    return extractedVersion?.map(piece => parseInt(piece, 10))[1] || null;
  }

  getPlatform(): Platform {
    return this.platform;
  }

  getSeekRate(): number[] {
    return [10, 20, 40, 60, 120];
  }

  getInteractiveAdExitDelay(): number {
    return 200;
  }

  async getDeviceInfo(): Promise<DeviceInfo> {
    return this.deviceInfo;
  }

  getCcSettings(): Partial<CcSettings> {
    return this._ccSettings;
  }

  isLowEndPlayerPerformance(): boolean {
    return false;
  }

  setLocalStorage<T>(key: string, value: T) {
    Storage.set(key, value);
  }

  getLocalStorage<T>(key: string) {
    // extra logic to fix previous double stringify bug
    const value = Storage.get(key) as T | null;

    if (typeof value !== 'string') return value;

    try {
      return JSON.parse(value);
    } catch (e) {
      // ignore already primitive value
    }
    return value;
  }

  getLocalStorageRaw<T>(key: string) {
    return Storage.get(key) as T | null;
  }

  removeLocalStorage(key: string) {
    Storage.remove(key);
  }

  setSessionStorage<T>(key: string, value: T) {
    const valueAsString = JSON.stringify(value);
    if (!sessionStorage) {
      console.warn('Session Storage Not Available, using local storage');
      Storage.set(key, valueAsString);
    } else {
      sessionStorage.setItem(key, valueAsString);
    }
  }

  getSessionStorage<T>(key: string) {
    let result: string | null;
    if (!sessionStorage) {
      console.warn('Session Storage Not Available, using local storage');
      result = Storage.get(key);
    } else {
      result = sessionStorage.getItem(key);
    }

    if (result === null) return null;

    return JSON.parse(result) as T;
  }

  removeSessionStorage(key: string) {
    if (!sessionStorage) {
      console.warn('Session Storage Not Available, using local storage');
      Storage.remove(key);
    } else {
      sessionStorage.removeItem(key);
    }
  }

  clearSessionStorage() {
    sessionStorage.clear();
  }

  shareHistoryPlatformName(): string {
    return '';
  }

  getIsLat(): boolean {
    return this.deviceInfo.isLat;
  }

  getAdvertisingId(): string | null {
    if (this.deviceInfo.isLat) return null;

    return this.deviceInfo.adId || null;
  }

  hoverDisabled(): boolean {
    return false; // if hovering is possible, we won't prevent it by default
  }

  /**
   * Handles deep link URL. Navigates `Home` if the URL cannot be parsed.
   * @param url URL in some form of `[http|https]://[DOMAIN]/#[RoutePath]/[...params]`
   */
  handleDeepLinkURL(url: string, params?: Router.NavigateArgs) {
    try {
      // Path will be the `[RoutePath]/[...params]` portion of the URL
      const hashIndex = url.indexOf('#');
      let path = hashIndex !== -1 ? url.substring(hashIndex) : url;

      // Remove `#` from path if present
      if (path.startsWith('#')) path = path.slice(1);

      const routePath = Object.values(RoutePath).find(routePath =>
        path.startsWith(routePath),
      );

      if (shouldShowLogin()) {
        AppData?.device.setSessionStorage('deepLinkAfterLogin', {
          path,
          params,
        });
        Router.navigate(RoutePath.LOGIN);
        return;
      }

      const homeHistoryEntry = [getHomeHistoryEntry()];
      const currentHistory = Router.getHistory();
      const baseHistory = currentHistory.length
        ? currentHistory
        : homeHistoryEntry;

      if (!routePath) {
        navigateHome();
      } else if (
        routePath === RoutePath.PLAY &&
        path.startsWith(`${RoutePath.PLAY}/live`)
      ) {
        const splitHash = path.split('/');

        let id: string | undefined;
        // If the length of the hash is three, assume the last param is the ID
        if (splitHash.length === 3) id = splitHash.pop();

        if (id) {
          Router.setHistory([
            ...baseHistory,
            getLiveEventHistoryEntry(id),
            getLivePlaybackHistoryEntry(id),
          ]);
          Router.back();
        } else {
          navigateHome();
        }
      } else {
        Router.setHistory(baseHistory);
        Router.navigate(path, params);
      }
    } catch (e) {
      // Navigate home if we're not able to parse and use the URL
      reportError({ error: e, userFacing: true, code: ErrorCode.c9 });
      navigateHome();
    }
  }

  // Expected Format: <constants.brandTokens.deepLinkScheme>://?type=series&show_id=law-and-order-toronto
  // eg. for CW: cwtv://?type=series&show_id=law-and-order-toronto
  handleBrandDeepLink(uri: string) {
    const url = new URL(uri);
    const actionType = url.searchParams.get('type');
    if (!actionType) return;

    const context: Router.NavigateArgs = {
      fromPageId: getPageIdFromHash(Router.getActiveHash() ?? '') as PageId,
    };

    let route: RoutePath | null = null;
    let id: string | null = '';

    switch (actionType) {
      case 'series': {
        route = RoutePath.SERIES_DETAIL;
        id = url.searchParams.get('show_id');
        break;
      }
      case 'movie': {
        route = RoutePath.MOVIE_DETAIL;
        id = url.searchParams.get('movie_id');
        break;
      }
      case 'live': {
        route = RoutePath.LIVE_EVENT_DETAIL;
        id = url.searchParams.get('live_id');
        break;
      }
      case 'hub': {
        route = RoutePath.CONTENT_HUB;
        id = url.searchParams.get('hub_id');
        break;
      }
      case 'channel': {
        route = RoutePath.EPG;
        id = url.searchParams.get('channel_id');
        break;
      }
      case 'play': {
        route = RoutePath.PLAY;
        id = url.searchParams.get('guid');
        break;
      }
      default:
        break;
    }

    if (id && route) {
      const deepLinkUrl = createDeepLinkUrl(route, id);
      this.handleDeepLinkURL(deepLinkUrl, context);
    }
  }

  addPrivacyChangeListener(callback: (optOut: boolean) => void) {
    this.privacyChangeListener = callback;
  }

  protected clearPrivacyChangeListener() {
    this.privacyChangeListener = undefined;
  }

  reportError(reportableError: ReportableError) {
    // do nothing
  }

  async getEnterKeyEvent(
    keyCodeMap: Record<number, string>,
    enterString: string,
  ) {
    const keyCode = Number(
      Object.keys(keyCodeMap).find(
        key => keyCodeMap[Number(key)] === enterString,
      ),
    );

    const event = new KeyboardEvent('keydown', {
      key: enterString,
      code: enterString,
      keyCode: keyCode,
      which: keyCode,
      bubbles: true,
      cancelable: true,
    });

    return getSimulatedKeyEvent(event);
  }

  addHoverStateChangedAction(key: string, action: (arg: boolean) => void) {
    // No-op;
  }

  removeHoverStateChangedAction(key: string) {
    // No-op;
  }

  getIsHovering() {
    return this.isHovering;
  }

  handleDebugKeyInputs(event: KeyboardEvent, app: App) {
    // No-op;
  }

  shouldCloseOnExit(event: KeyboardEvent): boolean {
    return true;
  }

  supportsParentalPin(): boolean {
    return false;
  }

  canMouseClick(): boolean {
    return false;
  }
}
